import React, { useState, useEffect } from 'react';
import { CodelcoLayout } from '../../components/codelco/CodelcoLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Truck, CheckCircle2, AlertCircle, FlaskConical, PlayCircle, PauseCircle, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CodelcoDashboardPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, []);

    const load = async () => {
        try {
            const r = await fetch(`${API_URL}/api/codelco/dashboard`, { headers });
            if (r.ok) setData(await r.json());
        } catch { /* ignore */ }
        setLoading(false);
    };

    if (loading || !data) {
        return <CodelcoLayout>
            <div className="grid md:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-32" />)}
            </div>
        </CodelcoLayout>;
    }

    const StatCard = ({ icon: Icon, label, value, color, hint }) => (
        <Card className="border-2">
            <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">{label}</span>
                    <Icon className={`h-5 w-5 ${color}`} />
                </div>
                <div className="text-3xl font-black">{value}</div>
                {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
            </CardContent>
        </Card>
    );

    return (
        <CodelcoLayout>
            <div className="space-y-6">
                <div>
                    <h1 className="text-3xl font-black tracking-tight">Dashboard</h1>
                    <p className="text-muted-foreground">Estado del integrador Codelco-Samtech</p>
                </div>

                {/* Status banners */}
                <div className="flex gap-3 flex-wrap">
                    {data.scheduler_activo ? (
                        <Badge className="bg-emerald-500 text-white border-0 text-sm px-3 py-1.5">
                            <PlayCircle className="h-4 w-4 mr-2" /> Scheduler activo
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-500 text-sm px-3 py-1.5">
                            <PauseCircle className="h-4 w-4 mr-2" /> Scheduler detenido
                        </Badge>
                    )}
                    {data.modo_prueba && (
                        <Badge className="bg-purple-500 text-white border-0 text-sm px-3 py-1.5">
                            <FlaskConical className="h-4 w-4 mr-2" /> Modo prueba (mock)
                        </Badge>
                    )}
                </div>

                <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                    <StatCard icon={Truck} label="Vehículos totales" value={data.total_vehiculos} color="text-blue-500" />
                    <StatCard icon={CheckCircle2} label="Integrados" value={data.integrados} color="text-emerald-500" hint={`${data.no_integrados} sin integrar`} />
                    <StatCard icon={CheckCircle2} label="Envíos OK (24h)" value={data.envios_ok_24h} color="text-emerald-500" />
                    <StatCard icon={AlertCircle} label="Errores (24h)" value={data.envios_err_24h} color="text-red-500" />
                </div>

                <Card className="border-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Tasa de éxito (24h)</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {data.porcentaje_exito === null ? (
                            <p className="text-muted-foreground">Sin envíos en las últimas 24h</p>
                        ) : (
                            <div className="space-y-2">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-5xl font-black">{data.porcentaje_exito}%</span>
                                    <span className="text-sm text-muted-foreground">de envíos exitosos</span>
                                </div>
                                <div className="h-3 bg-muted rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-emerald-500 to-green-600" style={{ width: `${data.porcentaje_exito}%` }} />
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {data.top_errores?.length > 0 && (
                    <Card className="border-2 border-red-500/30 bg-red-500/5">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-red-600"><AlertCircle className="h-5 w-5" /> Vehículos con más errores (24h)</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                {data.top_errores.map(e => (
                                    <div key={e.vehiculo_id} className="flex items-center justify-between p-3 bg-card border rounded-lg">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <span className="font-mono font-bold">{e.patente}</span>
                                            <span className="text-xs text-muted-foreground truncate">{e.ultimo_mensaje?.substring(0, 100)}</span>
                                        </div>
                                        <Badge variant="destructive">{e.errores} errores</Badge>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </CodelcoLayout>
    );
}
